import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import ContractDetailModal from './components/ContractDetailModal';
import SignContractModal from './components/SignContractModal';
import {
  radioContracts as contractsData,
  type RadioContract,
  type ContractStatus,
  getContractStatusLabel,
  getContractStatusColor,
} from '@/mocks/superAdminContracts';

type LoadingState = 'loading' | 'ready' | 'error';

const STATUS_FILTERS: { key: string; label: string; statuses: ContractStatus[] }[] = [
  { key: 'todas', label: 'Todas', statuses: [] },
  { key: 'pending_signature', label: 'Pendientes de firma', statuses: ['pending_signature'] },
  { key: 'signed', label: 'Firmados', statuses: ['signed'] },
  { key: 'needs_revision', label: 'En revisión', statuses: ['needs_revision'] },
  { key: 'drafts', label: 'Borradores', statuses: ['draft'] },
  { key: 'expired', label: 'Expirados', statuses: ['expired'] },
  { key: 'cancelled', label: 'Cancelados', statuses: ['cancelled'] },
];

function simulateLoad(): Promise<RadioContract[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.93) reject(new Error('Timeout al conectar con el servidor.'));
      else resolve(contractsData);
    }, 600);
  });
}

export default function SuperAdminContracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<RadioContract[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [activeStatus, setActiveStatus] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');

  const [viewContract, setViewContract] = useState<RadioContract | null>(null);
  const [signContract, setSignContract] = useState<RadioContract | null>(null);

  const [inlineMsg, setInlineMsg] = useState<{text: string; type: 'success'|'warning'} | null>(null);

  const showInline = useCallback((text: string, type: 'success'|'warning' = 'success') => {
    setInlineMsg({ text, type });
    setTimeout(() => setInlineMsg(null), 3500);
  }, []);

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad().then((d) => { setContracts(d); setLoadingState('ready'); }).catch((e) => { setErrorMsg(e.message || 'Error'); setLoadingState('error'); });
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    let r = contracts;
    const f = STATUS_FILTERS.find((x) => x.key === activeStatus);
    if (f && f.statuses.length > 0) r = r.filter((c) => f.statuses.includes(c.status));
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter((c) => c.radioName.toLowerCase().includes(q) || c.contractNumber.toLowerCase().includes(q) || c.frequency.toLowerCase().includes(q));
    }
    return r;
  }, [contracts, activeStatus, searchTerm]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { todas: contracts.length };
    STATUS_FILTERS.filter((f) => f.key !== 'todas').forEach((f) => { c[f.key] = contracts.filter((x) => f.statuses.includes(x.status)).length; });
    return c;
  }, [contracts]);

  const handleSignContract = (id: number, name: string, email: string, docId: string, writtenSig: string) => {
    setContracts((prev) => prev.map((c) => c.id === id ? { ...c, status: 'signed' as const, signedAt: new Date().toISOString(), signedByName: name, signedByEmail: email, signedByIdentifier: docId, contractVersion: c.contractVersion + (c.status === 'needs_revision' ? 1 : 0), revisionReason: null } : c));
    setSignContract(null);
    showInline(`Contrato firmado por ${name} correctamente`, 'success');
  };

  const handleSendForSignature = (c: RadioContract) => {
    setContracts((prev) => prev.map((x) => x.id === c.id ? { ...x, status: 'pending_signature' as const, generatedAt: new Date().toISOString() } : x));
    showInline(`Contrato ${c.contractNumber} enviado a firma`, 'success');
  };

  const handleCancel = (c: RadioContract) => {
    setContracts((prev) => prev.map((x) => x.id === c.id ? { ...x, status: 'cancelled' as const, nextRenewal: null } : x));
    showInline('Contrato cancelado', 'warning');
  };

  const handleRenew = (c: RadioContract) => {
    const now = new Date(); const exp = new Date(now); exp.setFullYear(exp.getFullYear() + 2);
    setContracts((prev) => prev.map((x) => x.id === c.id ? { ...x, status: 'pending_signature' as const, signedAt: null, signedByName: null, signedByEmail: null, signedByIdentifier: null, contractVersion: x.contractVersion + 1, generatedAt: now.toISOString(), expiresAt: exp.toISOString(), nextRenewal: exp.toISOString().split('T')[0], cancellationReason: null } : x));
    showInline(`Contrato ${c.contractNumber} renovado`, 'success');
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">

            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Contratos</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Contratos</h2>
                <p className="text-foreground-400 text-xs mt-0.5">{loadingState === 'ready' ? `${contracts.length} contratos` : 'Cargando...'}</p>
              </div>
              <div className="relative w-full sm:w-52">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar radio, contrato..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"><i className="ri-close-line text-foreground-500 text-[10px]"></i></button>}
              </div>
            </div>

            {/* Inline message */}
            {inlineMsg && (
              <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-medium w-fit ${inlineMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                <i className={`${inlineMsg.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-information-line text-amber-500'} text-xs`}></i>
                {inlineMsg.text}
              </div>
            )}

            {/* Status chips */}
            {loadingState === 'ready' && (
              <div className="flex flex-wrap items-center gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button key={f.key} onClick={() => setActiveStatus(f.key)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors cursor-pointer whitespace-nowrap ${activeStatus === f.key ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200'}`}>
                    {f.label} ({counts[f.key] ?? 0})
                  </button>
                ))}
              </div>
            )}

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                <div className="divide-y divide-background-100">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                      <div className="w-32 h-3 bg-background-200 rounded"></div>
                      <div className="w-24 h-3 bg-background-200 rounded"></div>
                      <div className="w-20 h-5 bg-background-200 rounded-full"></div>
                      <div className="w-16 h-3 bg-background-200 rounded ml-auto"></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p><p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button onClick={loadData} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
              </div>
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              filtered.length === 0 ? (
                <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-file-text-line text-foreground-300 text-2xl"></i></div>
                  <p className="text-foreground-800 text-sm font-semibold mb-1">Sin resultados</p>
                  <button onClick={() => { setActiveStatus('todas'); setSearchTerm(''); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer"><i className="ri-filter-off-line text-sm"></i>Limpiar filtros</button>
                </div>
              ) : (
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  {/* Desktop */}
                  <div className="hidden lg:block">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          {['Radio', 'Contrato', 'Estado', 'Firma', 'Vence', 'Acciones'].map((h) => (
                            <th key={h} className="px-4 py-2.5 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-background-100">
                        {filtered.map((c) => (
                          <tr key={c.id} onClick={() => setViewContract(c)} className={`hover:bg-background-100/50 transition-colors cursor-pointer ${c.status === 'cancelled' ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-3">
                              <p className="text-foreground-800 text-xs font-semibold">{c.radioName}</p>
                              <p className="text-foreground-400 text-[10px]">{c.frequency} · {c.planName}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-foreground-600 text-xs font-mono">{c.contractNumber}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getContractStatusColor(c.status)}`}>
                                {getContractStatusLabel(c.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {c.signedAt ? (
                                <span className="text-emerald-600 text-[10px] font-semibold flex items-center gap-1"><i className="ri-check-double-line text-[9px]"></i>{c.signedByName}</span>
                              ) : (
                                <span className="text-amber-500 text-[10px] font-medium">Pendiente</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-[11px] ${c.nextRenewal && new Date(c.nextRenewal) < new Date() ? 'text-red-500 font-semibold' : 'text-foreground-500'}`}>
                                {c.nextRenewal ? new Date(c.nextRenewal + 'T00:00:00Z').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => setViewContract(c)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                                <i className="ri-eye-line text-foreground-500 text-xs"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile */}
                  <div className="lg:hidden divide-y divide-background-100">
                    {filtered.map((c) => (
                      <div key={c.id} onClick={() => setViewContract(c)} className={`p-4 cursor-pointer hover:bg-background-100/50 transition-colors ${c.status === 'cancelled' ? 'opacity-50' : ''}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground-800 text-xs font-semibold truncate">{c.radioName}</p>
                            <p className="text-foreground-400 text-[10px]">{c.contractNumber}</p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${getContractStatusColor(c.status)}`}>
                            {getContractStatusLabel(c.status)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-foreground-500 text-[10px]">
                            {c.signedAt ? `Firmado por ${c.signedByName}` : 'Sin firmar'} · Vence {c.nextRenewal ? new Date(c.nextRenewal + 'T00:00:00Z').toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); setViewContract(c); }} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Ver detalle">
                            <i className="ri-eye-line text-foreground-500 text-xs"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>
        </main>
      </div>

      {/* Detail Modal */}
      {viewContract && (
        <ContractDetailModal
          contract={viewContract}
          onClose={() => setViewContract(null)}
          onNavigate={(path: string) => navigate(path)}
          onSign={(c) => { setViewContract(null); setSignContract(c); }}
          onSendForSignature={handleSendForSignature}
          onCancel={handleCancel}
          onRenew={handleRenew}
        />
      )}

      {/* Sign Modal */}
      {signContract && (
        <SignContractModal contract={signContract} onClose={() => setSignContract(null)} onConfirm={handleSignContract} />
      )}

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Contratos</p>
      </div>
    </div>
  );
}